// One master volume for every sound the app plays (product owner, 2026-10-31): the
// interviewer's questions and follow-ups, the warm-up, the candidate's own warm-up
// playback and the voice sample. It sets each audio player's own volume, so it never
// touches the microphone or what is recorded. Remembered in this browser only.

const STORAGE_KEY = "mec_master_volume";
const DEFAULT = { level: 1, muted: false };

let state = load();
const listeners = new Set();
const playing = new Set(); // players not in the page (new Audio()) that are playing
let installed = false;

function load() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null");
    if (saved && typeof saved.level === "number") {
      return { level: Math.min(1, Math.max(0, saved.level)), muted: Boolean(saved.muted) };
    }
  } catch {
    /* private window or blocked storage: the default applies */
  }
  return { ...DEFAULT };
}

export function getVolume() {
  return state;
}

export function effectiveVolume(s = state) {
  return s.muted ? 0 : s.level;
}

function applyTo(el) {
  try {
    el.volume = effectiveVolume();
  } catch {
    /* a player that refuses volume changes keeps its own */
  }
}

function applyEverywhere() {
  if (typeof document !== "undefined") document.querySelectorAll("audio, video").forEach(applyTo);
  playing.forEach(applyTo);
}

export function setVolume(next) {
  state = {
    level: next.level === undefined ? state.level : Math.min(1, Math.max(0, Number(next.level) || 0)),
    muted: next.muted === undefined ? state.muted : Boolean(next.muted),
  };
  // Raising the level from silent unmutes, as on any device.
  if (next.level !== undefined && next.muted === undefined && state.level > 0) state.muted = false;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* not remembered, still applied */
  }
  applyEverywhere();
  listeners.forEach((fn) => fn(state));
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Every player starts at the master volume: play() is wrapped once, so players created
// anywhere (in the page or with new Audio()) follow it, including ones already playing.
export function installMasterVolume() {
  if (installed || typeof window === "undefined" || !window.HTMLMediaElement) return;
  installed = true;
  const proto = window.HTMLMediaElement.prototype;
  const play = proto.play;
  proto.play = function playAtMasterVolume(...args) {
    applyTo(this);
    if (!this.isConnected) {
      playing.add(this);
      const done = () => playing.delete(this);
      this.addEventListener("ended", done, { once: true });
      this.addEventListener("pause", done, { once: true });
    }
    return play.apply(this, args);
  };
}
